import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GrCut } from 'react-icons/gr';
import { IoClose } from 'react-icons/io5';
import { MdCheckBox, MdOutlineCheckBoxOutlineBlank } from 'react-icons/md';
import SearchBar from './SearchBar';
import { Button } from '../ui/Button';

const speakerColorPalette = [
  '#7a8fa6',
  '#b08a86',
  '#8a9e7e',
  '#9a8db8',
  '#b5899e',
  '#b09a6e',
];

const formatTranscriptTimestamp = (deciSeconds) => {
  const safe = typeof deciSeconds === 'number' ? deciSeconds : 0;
  const totalSeconds = Math.max(0, Math.floor(safe / 10));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
  const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  if (hours > 0) return `${String(hours).padStart(2, '0')}:${minutes}:${seconds}`;
  return `${minutes}:${seconds}`;
};

const normalizeTranscriptDisplayText = (text) => text.replace(/\s+/g, ' ').trim();
const hasMeaningfulTranscriptText = (text) =>
  /[A-Za-z0-9]/.test(normalizeTranscriptDisplayText(text));

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (text, term) => {
  if (!term) return [text];
  const escaped = escapeRegex(term);
  const regex = new RegExp(escaped, 'gi');
  let lastIndex = 0;
  const result = [];
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) result.push(text.slice(lastIndex, match.index));
    result.push(
      <span key={`hl-${key++}`} className="bg-yellow-200 inline whitespace-normal">
        {match[0]}
      </span>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) result.push(text.slice(lastIndex));
  return result;
};

const getSpeakerInitials = (speaker) => {
  if (!speaker) return '?';
  const words = speaker.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return speaker.substring(0, 2).toUpperCase();
};

const RichTranscript = ({
  transcript,
  onGoToTimeStamp,
  initialSearchTerm,
  searchText,
  allowClip = false,
}) => {
  const navigate = useNavigate();
  const { state, videoId } = useParams();
  const [searcherResult, setSearcherResult] = useState([]);
  const [paragraphPosition, setParagraphPosition] = useState(0);
  const [searchTerm, setSearchTerm] = useState(null);
  const [isClipMode, setIsClipMode] = useState(false);
  const [clipStart, setClipStart] = useState(null);
  const [clipEnd, setClipEnd] = useState(null);
  const containerRef = useRef(null);
  const speakerColorMap = useRef({});

  const closeClipMode = () => {
    setIsClipMode(false);
    setClipStart(null);
    setClipEnd(null);
  };

  useEffect(() => {
    if (clipStart && clipEnd && videoId) {
      const a = clipStart.startTime <= clipEnd.startTime ? clipStart : clipEnd;
      const b = clipStart.startTime <= clipEnd.startTime ? clipEnd : clipStart;
      const startSec = Math.round(a.startTime / 10);
      const endSec = Math.round(b.endTime / 10);
      navigate(
        `/${state}/create-clip/${videoId}?start=${startSec}&end=${endSec}&sp=${a.position}&ep=${b.position}`
      );
      setClipStart(null);
      setClipEnd(null);
      setIsClipMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipStart, clipEnd]);

  const handleSelectionClick = (entry) => {
    if (!isClipMode) return false;
    if (!clipStart) {
      setClipStart(entry);
    } else {
      setClipEnd(entry);
    }
    return true;
  };

  useEffect(() => {
    speakerColorMap.current = {};
  }, [transcript]);

  const highlightedWordPositions = useMemo(() => {
    const map = new Map();
    for (const item of searcherResult) {
      if (!item || item.paragraphIndex == null) continue;
      let set = map.get(item.paragraphIndex);
      if (!set) {
        set = new Set();
        map.set(item.paragraphIndex, set);
      }
      if (item.positions) {
        for (const pos of item.positions) set.add(pos);
      }
    }
    return map;
  }, [searcherResult]);

  const highlightedParagraphIndices = useMemo(() => {
    const set = new Set();
    for (const item of searcherResult) {
      if (item && item.paragraphIndex != null) set.add(item.paragraphIndex);
    }
    return set;
  }, [searcherResult]);

  const speakerGroups = useMemo(() => {
    const speakerTranscript = transcript?.speakerTranscript;
    if (!speakerTranscript || speakerTranscript.length === 0) return [];

    const groups = [];
    for (let index = 0; index < speakerTranscript.length; index++) {
      const segment = speakerTranscript[index];
      if (!hasMeaningfulTranscriptText(segment.text)) continue;

      const previous = groups[groups.length - 1];
      const previousKey = previous?.speakerKey ?? '';
      const segKey = (segment.speaker ?? '').trim().toLowerCase();

      if (previous && previousKey === segKey) {
        previous.segments.push({ index, segment });
        continue;
      }

      groups.push({
        speaker: segment.speaker,
        speakerKey: segKey,
        segments: [{ index, segment }],
      });
    }
    return groups;
  }, [transcript]);

  const getSpeakerColor = (speaker) => {
    const key = (speaker ?? '').trim().toLowerCase();
    const map = speakerColorMap.current;
    if (!map[key]) {
      map[key] = speakerColorPalette[Object.keys(map).length % speakerColorPalette.length];
    }
    return map[key];
  };

  const scrollToParagraph = (paragraphNumber) => {
    const el = document.getElementById(`p-${paragraphNumber}`);
    const container = containerRef.current;
    if (el && container) {
      container.scrollTo({ top: el.offsetTop - container.offsetTop, behavior: 'smooth' });
    }
  };

  const handleSearch = (term) => {
    if (!term) {
      setSearcherResult([]);
      return;
    }
    const lowerTerm = term.toLowerCase();
    if (transcript?.wordTranscript?.length > 0) {
      const result = transcript.wordTranscript.reduce((acc, p, index) => {
        const searchWords = term.split(' ');
        const positions = [];
        for (let i = 0; i < p.length - searchWords.length + 1; i++) {
          let found = true;
          const tempPositions = [];
          for (let j = 0; j < searchWords.length; j++) {
            if (
              !p[i + j].word
                .toLowerCase()
                .replace(/[^\w\s]/g, '')
                .startsWith(searchWords[j].toLowerCase().replace(/[^\w\s]/g, ''))
            ) {
              found = false;
              break;
            }
            tempPositions.push(i + j);
          }
          if (found) positions.push(...tempPositions);
        }
        if (positions.length > 0) acc.push({ paragraphIndex: index, positions });
        return acc;
      }, []);
      setParagraphPosition(0);
      setSearcherResult(result);
      return;
    }

    if (transcript?.speakerTranscript?.length > 0) {
      const result = transcript.speakerTranscript.reduce((acc, segment, index) => {
        if (segment.text.toLowerCase().includes(lowerTerm)) {
          acc.push({ paragraphIndex: index, positions: [] });
        }
        return acc;
      }, []);
      setParagraphPosition(0);
      setSearcherResult(result);
    }
  };

  useEffect(() => {
    if (searchText) {
      setSearchTerm(searchText);
      handleSearch(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    if (initialSearchTerm && transcript && searchTerm === null) {
      setSearchTerm(initialSearchTerm);
      handleSearch(initialSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchTerm, transcript]);

  useEffect(() => {
    const current = searcherResult[paragraphPosition];
    if (!current) return;
    scrollToParagraph(current.paragraphIndex);

    if (transcript) {
      let timestamp = 0;
      if (transcript.wordTranscript?.length > 0 && current.positions?.length > 0) {
        const wordIdx = current.positions[0];
        const word = transcript.wordTranscript[current.paragraphIndex]?.[wordIdx];
        if (word) timestamp = word.startTime;
      } else if (transcript.speakerTranscript?.length > 0) {
        const segment = transcript.speakerTranscript[current.paragraphIndex];
        if (segment) timestamp = segment.startTime;
      }
      if (timestamp > 0 && onGoToTimeStamp) onGoToTimeStamp(timestamp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraphPosition, searcherResult]);

  const handleChangeParagraphLocation = (direction) => {
    if (direction === 'down' && paragraphPosition < searcherResult.length - 1) {
      setParagraphPosition(paragraphPosition + 1);
    } else if (direction === 'up' && paragraphPosition > 0) {
      setParagraphPosition(paragraphPosition - 1);
    }
  };

  const hasTranscript =
    (transcript?.wordTranscript?.length ?? 0) > 0 ||
    (transcript?.speakerTranscript?.length ?? 0) > 0;

  return (
    <div>
      <div className="mb-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-lg font-medium text-stone-800">Video Transcript</h2>
          {allowClip && (
            <Button
              variant={isClipMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => (isClipMode ? closeClipMode() : setIsClipMode(true))}
              title={isClipMode ? 'Exit clip mode' : 'Create clip — select start and end'}
            >
              <GrCut className="h-4 w-4" />
              {isClipMode ? 'Cancel' : 'Cut Clip'}
            </Button>
          )}
        </div>
        <SearchBar
          handleSearch={handleSearch}
          counter={searcherResult.length}
          location={paragraphPosition + 1}
          onChangeLocation={handleChangeParagraphLocation}
          placeholder="Search..."
          search={searchTerm}
          setSearch={setSearchTerm}
          autoSearch
        />
      </div>
      <div className="space-y-3 h-[calc(100vh-320px)] overflow-y-auto" ref={containerRef}>
        {!hasTranscript && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            No transcript available for this hearing yet.
          </div>
        )}

        {transcript?.wordTranscript?.length > 0 &&
          transcript.wordTranscript.map((paragraph, i) => (
            <React.Fragment key={i}>
              {paragraph.length > 0 && (
                <p className="w-full flex flex-wrap" key={`p-${i}`} id={`p-${i}`}>
                  {paragraph.map((wordObj, j) => {
                    const isHighlighted = highlightedWordPositions.get(i)?.has(j) ?? false;
                    return (
                      <span
                        key={`span-${i}-${j}`}
                        id={`span-${i}-${j}`}
                        role="button"
                        tabIndex={0}
                        title={`${formatTranscriptTimestamp(wordObj.startTime)} - ${formatTranscriptTimestamp(wordObj.endTime)}`}
                        className={`whitespace-pre-wrap hover:bg-blue-100 cursor-pointer px-1 relative ${
                          isHighlighted ? 'bg-blue-200' : ''
                        } ${
                          isClipMode &&
                          ((clipStart && clipStart.position === `${i}-${j}`) ||
                            (clipEnd && clipEnd.position === `${i}-${j}`))
                            ? 'bg-amber-200'
                            : ''
                        }`}
                        onClick={() => {
                          const entry = {
                            startTime: wordObj.startTime,
                            endTime: wordObj.endTime,
                            position: `${i}-${j}`,
                          };
                          if (handleSelectionClick(entry)) return;
                          onGoToTimeStamp && onGoToTimeStamp(wordObj.startTime);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const entry = {
                              startTime: wordObj.startTime,
                              endTime: wordObj.endTime,
                              position: `${i}-${j}`,
                            };
                            if (handleSelectionClick(entry)) return;
                            onGoToTimeStamp && onGoToTimeStamp(wordObj.startTime);
                          }
                        }}
                      >
                        {wordObj.word}
                      </span>
                    );
                  })}
                </p>
              )}
            </React.Fragment>
          ))}

        {isClipMode && (
          <div className="sticky bottom-2 z-10 mx-auto w-fit max-w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 shadow-lg">
            <div className="flex items-center gap-3">
              <GrCut className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-slate-700">
                {!clipStart
                  ? 'Click a transcript segment to set the start.'
                  : 'Now click a segment to set the end.'}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                Start{' '}
                {clipStart ? (
                  <MdCheckBox className="h-4 w-4 text-blue-600" />
                ) : (
                  <MdOutlineCheckBoxOutlineBlank className="h-4 w-4" />
                )}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                End{' '}
                {clipEnd ? (
                  <MdCheckBox className="h-4 w-4 text-blue-600" />
                ) : (
                  <MdOutlineCheckBoxOutlineBlank className="h-4 w-4" />
                )}
              </span>
              <button
                type="button"
                onClick={closeClipMode}
                className="ml-2 rounded p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Cancel clip selection"
              >
                <IoClose className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {speakerGroups.map((group) => {
          const firstSegment = group.segments[0];
          const groupKey = firstSegment ? `speaker-group-${firstSegment.index}` : group.speaker;
          const segmentColor = getSpeakerColor(group.speaker);

          return (
            <div className="flex gap-3" key={groupKey}>
              <div className="flex flex-col items-center gap-1.5 pt-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white shrink-0"
                  style={{ backgroundColor: segmentColor }}
                >
                  {getSpeakerInitials(group.speaker)}
                </div>
                <div
                  className="w-1 rounded-full flex-1"
                  style={{ backgroundColor: segmentColor, opacity: 0.4 }}
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 overflow-hidden rounded-2xl border border-stone-200/60 bg-[#fdfbf7] shadow-sm shadow-stone-200/40">
                <div className="px-4 py-3 text-sm leading-8 text-stone-700 whitespace-normal">
                  {group.segments.map(({ index, segment }) => {
                    const isHighlighted = highlightedParagraphIndices.has(index);
                    const startDeciseconds =
                      typeof segment.startTime === 'number' ? segment.startTime : 0;
                    const endDeciseconds =
                      typeof segment.endTime === 'number' ? segment.endTime : startDeciseconds;
                    const displayText = normalizeTranscriptDisplayText(segment.text);
                    if (!displayText) return null;

                    const segmentHighlightClass = isHighlighted
                      ? 'bg-blue-50/80 shadow-[inset_0_0_0_1px_rgba(191,219,254,0.9)]'
                      : 'hover:bg-stone-50/70';

                    return (
                      <React.Fragment key={`span-${index}`}>
                        <span
                          id={`p-${index}`}
                          className="relative -top-20 inline-block h-0 w-0 overflow-hidden align-baseline"
                          aria-hidden="true"
                        />
                        <span
                          id={`span-${index}`}
                          role="button"
                          tabIndex={0}
                          title={`${formatTranscriptTimestamp(startDeciseconds)} - ${formatTranscriptTimestamp(endDeciseconds)}`}
                          className={`inline cursor-pointer rounded-sm px-0.5 py-0.5 transition-colors duration-150 box-decoration-clone ${segmentHighlightClass} ${
                            isClipMode &&
                            ((clipStart && clipStart.position === `${index}-0`) ||
                              (clipEnd && clipEnd.position === `${index}-0`))
                              ? 'bg-amber-200'
                              : ''
                          }`}
                          onClick={() => {
                            const entry = {
                              startTime: startDeciseconds,
                              endTime: endDeciseconds,
                              position: `${index}-0`,
                            };
                            if (handleSelectionClick(entry)) return;
                            onGoToTimeStamp && onGoToTimeStamp(startDeciseconds);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              const entry = {
                                startTime: startDeciseconds,
                                endTime: endDeciseconds,
                                position: `${index}-0`,
                              };
                              if (handleSelectionClick(entry)) return;
                              onGoToTimeStamp && onGoToTimeStamp(startDeciseconds);
                            }
                          }}
                        >
                          {highlightText(displayText, searchTerm)}
                        </span>{' '}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RichTranscript;
